import { all, call, put, select, takeEvery } from 'redux-saga/effects';
import { fetchEntityIfNeeded } from '@classpass/saga-tools';
import * as logger from 'universal-logger';
import {
  dashFeatureById,
  promotionByAlias,
  promotionByAliasAndCoordinates,
  userInactivePermissions,
  userProfile,
} from 'redux/saga/entities/bound';
import { memberIsActive } from 'redux/saga/helpers/member';
import { selectMsaId } from 'redux/modules/features/homepage';
import { selectUserDetectedLocation, selectUserSelectedLocation } from 'redux/modules/locale';
import { setPagePromotion } from 'redux/modules/page';
import { showModal } from 'redux/modules/modals';
import { isLoggedIn } from 'redux/saga/selectors';
import { deepExtract } from 'helpers/deep';
import { isEmpty } from 'helpers/verify';
import * as ArrayUtils from 'helpers/array';
import { getMsa } from 'containers/LocationModal/location.saga';
import { GET_CLASSPASS_DEFAULT_PROMOTION_ALIAS, PROMOTIONS_CMS_MODULES_ENABLED } from 'constants/promotions';
import { hasSeenOfferModal, markOfferModalAsSeen } from 'helpers/promotions';

// NOTE: If a user selects their location, we override the promo coming from the user's detected location
//       and use the promo fetched by the old endpoint. This will eventually get removed but moving off
//       of MSA's is a longer process. Here is the geo-targeting promotion doc for more information.
// https://docs.google.com/document/d/1PVZPhgdVQNC0YuiaENp4i8uNkUnmGEvYdWhE6XWJfU0/edit#heading=h.ak8on47d7r28
const isGeotargetedPromotion = promotion => !!deepExtract('offers.0.location.id', promotion);

export function selectActivePromotion(state, promoAlias = GET_CLASSPASS_DEFAULT_PROMOTION_ALIAS) {
  const msaId = selectMsaId(state);
  const msaPromoKey = promotionByAlias.defaultKey(null, promoAlias, msaId);
  const msaPromoData = promotionByAlias.data(state, msaPromoKey) || {};

  const selectedLocation = selectUserSelectedLocation(state);
  const selectedPromoKey =
    !isEmpty(selectedLocation) && promotionByAliasAndCoordinates.defaultKey(null, promoAlias, selectedLocation);
  const selectedPromoData = (selectedPromoKey && promotionByAliasAndCoordinates.data(state, selectedPromoKey)) || {};

  const detectedLocation = selectUserDetectedLocation(state);
  const detectedPromoKey =
    !isEmpty(detectedLocation) && promotionByAliasAndCoordinates.defaultKey(null, promoAlias, detectedLocation);
  const detectedPromoData = (detectedPromoKey && promotionByAliasAndCoordinates.data(state, detectedPromoKey)) || {};

  const promotionsHaveSameOffers = ArrayUtils.containsSameBy(
    selectedPromoData.offers,
    detectedPromoData.offers,
    offer => offer?.id
  );

  let activePromotion;
  if (
    (isEmpty(selectedLocation) && !isEmpty(detectedPromoData)) ||
    (isGeotargetedPromotion(selectedPromoData) && promotionsHaveSameOffers)
  ) {
    activePromotion = detectedPromoData;
  } else {
    activePromotion = msaPromoData;
  }

  return activePromotion;
}

export function* loadDetectedPromotion(client, promoAlias) {
  const detectedLocation = yield select(selectUserDetectedLocation);
  if (!isEmpty(detectedLocation)) {
    try {
      yield call(fetchEntityIfNeeded, promotionByAliasAndCoordinates, [client, promoAlias, detectedLocation]);
    } catch (err) {
      if (err.httpStatus !== 404) {
        yield call(logger.warn, `Failed to find promotion for alias ${promoAlias} based on detected location`, err);
      }
    }
  }
}

export function* loadMsaPromotion(client, promoAlias) {
  const msa = yield call(getMsa, client, {}, { redirectBackOnMissing: false });
  try {
    yield call(fetchEntityIfNeeded, promotionByAlias, [client, promoAlias, msa.id]);
  } catch (err) {
    if (err.httpStatus !== 404) {
      yield call(logger.warn, `Failed to find promotion for alias ${promoAlias} based on msa`, err);
    }
  }
}

export function* loadSelectedPromotion(client, promoAlias) {
  const selectedLocation = yield select(selectUserSelectedLocation);
  if (isEmpty(selectedLocation)) {
    return;
  }
  try {
    yield call(fetchEntityIfNeeded, promotionByAliasAndCoordinates, [client, promoAlias, selectedLocation]);
  } catch (err) {
    if (err.httpStatus !== 404) {
      yield call(
        logger.warn,
        `Failed to find promotion for alias ${promoAlias} based on selected location's coordinates`,
        err
      );
    }
  }
}

// We make all three calls here since we will fall back according to the logic in selectActivePromotion
export function* loadActivePromotion(client, promoAlias) {
  yield all([
    call(loadMsaPromotion, client, promoAlias),
    call(loadSelectedPromotion, client, promoAlias),
    call(loadDetectedPromotion, client, promoAlias),
  ]);
}

export function* isActivePromotionGeotargeted(client, promoAlias) {
  yield call(loadActivePromotion, client, promoAlias);
  const activePromo = yield select(selectActivePromotion);
  return isGeotargetedPromotion(activePromo);
}

export function* doCheckGeotargetedOfferModal(client, { data: promoAlias }) {
  const doesGeotargetedPromotionExist = yield call(isActivePromotionGeotargeted, client, promoAlias);
  if (doesGeotargetedPromotionExist && !(yield call(hasSeenOfferModal))) {
    yield put(showModal('OfferModal'));
    yield call(markOfferModalAsSeen, client);
  }
}

export function* shouldDisallowOfferExperience(client) {
  const isPromotionsCmsEnabled = yield call(fetchEntityIfNeeded, dashFeatureById, [
    client,
    PROMOTIONS_CMS_MODULES_ENABLED,
  ]);
  if (!isPromotionsCmsEnabled) {
    return true;
  }

  const isMemberActive = yield call(memberIsActive, client);
  if (isMemberActive) {
    return true;
  }

  if (yield select(isLoggedIn)) {
    const profile = yield call(fetchEntityIfNeeded, userProfile, [client]);
    const permissions = yield call(fetchEntityIfNeeded, userInactivePermissions, [client, profile.id]);
    return permissions.disallow_winback_offers;
  }

  return false;
}

export function* watchPagePromotionForModal(client) {
  if (yield call(shouldDisallowOfferExperience, client)) {
    return;
  }
  yield takeEvery(setPagePromotion().type, doCheckGeotargetedOfferModal, client);
}
